/**
 * Sdílená komunikace s legacy top-frame agendou přes postMessage.
 *
 * Protokol: top frame poslouchá `{ action, data }`, odpovídá `{ response, data }`.
 * Hook `useLegacyMessaging` je nízkoúrovňový základ; typované hooky jsou pro běžné použití.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getMagicDesktopFrame } from './magicDesktopFrame';

// ── Interní typy protokolu ─────────────────────────────────────────────────

type LegacyAction = string;

export interface LegacyRawMessage {
  response: string;
  /** Vráceno top-framem pro spárování s odeslaným requestem. */
  requestId?: string;
  /** Pokud je nastaven, top-frame signalizuje chybu — agenda by měla spadnout s tímto textem. */
  error?: string;
  data?: {
    success?: boolean;
    id?: string | number;
    /**
     * Vybraná id z `form/list`. Magic serializuje camelCase → `selectedIds`; `selectedids`
     * (vše malými) ponecháno pro toleranci starších top-framů. Čti přes {@link readSelectedIds}.
     */
    selectedIds?: readonly (string | number)[];
    selectedids?: readonly (string | number)[];
  };
}

/** Tolerantní čtení vybraných id z `form/list` odpovědi (camelCase i legacy lowercase). */
export function readSelectedIds(data: LegacyRawMessage['data']): readonly (string | number)[] {
  const raw = data?.selectedIds ?? data?.selectedids;
  return Array.isArray(raw) ? raw : [];
}

export function isLegacyRawMessage(v: unknown): v is LegacyRawMessage {
  if (!v || typeof v !== 'object') return false;
  return typeof (v as { response?: unknown }).response === 'string';
}

// ── Výběrový styl pro form/list ────────────────────────────────────────────

export enum LegacySelectionStyle {
  None = 0,
  Single = 1,
  Multi = 2,
}

// ── Veřejné response typy ─────────────────────────────────────────────────

export interface LegacyNewResponse {
  id: string | number;
}
export interface LegacyEditResponse {
  id: string | number;
}
export interface LegacyListResponse {
  selectedIds: readonly (string | number)[];
}
export type LegacyAnyResponse = Partial<LegacyEditResponse> & Partial<LegacyListResponse>;

/** Po této době bez odpovědi zrušíme pending (např. Magic control neimplementuje akci). */
const LEGACY_RESPONSE_TIMEOUT_MS = 60_000;

// ── Core hook ──────────────────────────────────────────────────────────────

/**
 * Nízkoúrovňový hook – posílá postMessage do top-framu a naslouchá odpovědi.
 * `callbackRef` zajišťuje, že handler vždy vidí aktuální closure bez nutnosti
 * re-subscribe, když se změní závislosti consumera.
 *
 * Pro běžné použití preferuj typované hooky níže.
 */
export function useLegacyMessaging() {
  const [pending, setPending] = useState<{ action: LegacyAction; requestId: string } | null>(null);
  const [fatalError, setFatalError] = useState<Error | null>(null);
  const callbackRef = useRef<((msg: LegacyRawMessage) => void) | null>(null);

  // Způsobí pád agendy zachytitelný error boundary.
  if (fatalError) throw fatalError;

  useEffect(() => {
    if (!pending) return;

    const timeoutId = window.setTimeout(() => {
      console.warn('[legacyMagic] response timeout', {
        action: pending.action,
        requestId: pending.requestId,
      });
      setPending(null);
    }, LEGACY_RESPONSE_TIMEOUT_MS);

    const handler = (event: MessageEvent) => {
      const msg = event.data;
      if (!isLegacyRawMessage(msg)) return;
      // Primárně párujeme přes requestId; fallback na action pro starší top-framy bez requestId.
      const matched = msg.requestId
        ? msg.requestId === pending.requestId
        : msg.response === pending.action;
      if (!matched) {
        console.log('[legacyMagic] response ignored (no match)', { msg, pending });
        return;
      }
      console.log('[legacyMagic] response matched', { msg, pending });
      setPending(null);
      if (msg.error) {
        console.error('[legacyMagic] response error', {
          action: pending.action,
          requestId: pending.requestId,
          error: msg.error,
        });
        setFatalError(new Error(msg.error));
        return;
      }
      callbackRef.current?.(msg);
    };
    window.addEventListener('message', handler);
    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('message', handler);
    };
  }, [pending]);

  const send = useCallback(
    (
      action: LegacyAction,
      payload: Record<string, unknown>,
      onResponse: (msg: LegacyRawMessage) => void
    ) => {
      const requestId = crypto.randomUUID();
      console.log('[legacyMagic] send', { action, requestId, payload });
      callbackRef.current = onResponse;
      setPending({ action, requestId });
      try {
        const target = getMagicDesktopFrame();
        if (!target) {
          console.error('[legacyMagic] no Magic desktop frame available', { action, requestId });
          setPending(null);
          return;
        }
        target.postMessage({ action, requestId, data: payload }, '*');
      } catch (err) {
        console.error('[legacyMagic] send failed', { action, requestId, error: err });
        setPending(null);
      }
    },
    []
  );

  return { send, isPending: pending !== null };
}

// ── useLegacyForm – libovolný formulář zadaný názvem ─────────────────────

/**
 * Otevírá libovolný legacy formulář identifikovaný názvem (`Form`).
 * Akce je vždy `"form"` — form sám určuje, co udělá.
 * Callback `onClosed` je vyvolán přes ref, nemusí být stabilní reference.
 */
export function useLegacyForm(form: string) {
  const { send, isPending } = useLegacyMessaging();

  const openForm = useCallback(
    (
      parameters?: Record<string, unknown>,
      onClosed?: (result: LegacyAnyResponse | null) => void
    ) => {
      send('form', { Form: form, ...parameters }, (msg) => {
        if (!msg.data?.success) {
          console.log('[legacyMagic] useLegacyForm closed', { form, result: null });
          onClosed?.(null);
          return;
        }
        const result: LegacyAnyResponse = {
          id: msg.data.id,
          selectedIds: readSelectedIds(msg.data),
        };
        console.log('[legacyMagic] useLegacyForm closed', { form, result });
        onClosed?.(result);
      });
    },
    [send, form]
  );

  return { openForm, isPending };
}

// ── ObjectCategory hooky ──────────────────────────────────────────────────

/** Otevírá formulář pro vytvoření nového záznamu zadané kategorie (`form/new`). */
export function useLegacyNewForm(objectCategory: string) {
  const { send, isPending } = useLegacyMessaging();

  const openNew = useCallback(
    (
      parameters?: Record<string, unknown>,
      onClosed?: (result: LegacyNewResponse | null) => void
    ) => {
      send('form/new', { ObjectCategory: objectCategory, ...parameters }, (msg) => {
        const id = msg.data?.success && msg.data.id != null ? msg.data.id : null;
        const result = id != null ? { id } : null;
        console.log('[legacyMagic] useLegacyNewForm closed', { objectCategory, result });
        onClosed?.(result);
      });
    },
    [send, objectCategory]
  );

  return { openNew, isPending };
}

/** Otevírá formulář pro úpravu záznamu zadané kategorie (`form/edit`). */
export function useLegacyEditForm(objectCategory: string) {
  const { send, isPending } = useLegacyMessaging();

  const openEdit = useCallback(
    (
      id: string | number,
      parameters?: Record<string, unknown>,
      onClosed?: (result: LegacyEditResponse | null) => void
    ) => {
      send('form/edit', { ObjectCategory: objectCategory, ID: id, ...parameters }, (msg) => {
        const rid = msg.data?.success && msg.data.id != null ? msg.data.id : null;
        const result = rid != null ? { id: rid } : null;
        console.log('[legacyMagic] useLegacyEditForm closed', { objectCategory, id, result });
        onClosed?.(result);
      });
    },
    [send, objectCategory]
  );

  return { openEdit, isPending };
}

/** Otevírá výběrový seznam záznamu zadané kategorie (`form/list`). */
export function useLegacyListForm(objectCategory: string) {
  const { send, isPending } = useLegacyMessaging();

  const openList = useCallback(
    (
      selectionStyle: LegacySelectionStyle,
      parameters?: Record<string, unknown>,
      onClosed?: (result: LegacyListResponse | null) => void
    ) => {
      send(
        'form/list',
        { ObjectCategory: objectCategory, SelectionStyle: selectionStyle, ...parameters },
        (msg) => {
          if (!msg.data?.success) {
            console.log('[legacyMagic] useLegacyListForm closed', {
              objectCategory,
              selectionStyle,
              result: null,
            });
            onClosed?.(null);
            return;
          }
          const ids = readSelectedIds(msg.data);
          const result = { selectedIds: ids };
          console.log('[legacyMagic] useLegacyListForm closed', {
            objectCategory,
            selectionStyle,
            result,
          });
          onClosed?.(result);
        }
      );
    },
    [send, objectCategory]
  );

  return { openList, isPending };
}

// ── Magic desktop frame target — viz magicDesktopFrame.ts ─────────────────
export {
  getMagicDesktopFrame,
  useMagicDesktopFrame,
  registerMagicControlFrame,
} from './magicDesktopFrame';
